const MAX_VISIBLE = 4;
const DEFAULT_DURATION_MS = 3000;
const PENDING_TOAST_STORAGE_KEY = "ouro:pending-toast";

let toasts = [];
const listeners = new Set();
let idCounter = 0;
const timers = new Map();

function emit() {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch (err) {
            console.error("[toastStore] listener error", err);
        }
    });
}

function clearTimer(id) {
    const timer = timers.get(id);
    if (timer != null) {
        clearTimeout(timer);
        timers.delete(id);
    }
}

function scheduleAutoDismiss(id, durationMs) {
    clearTimer(id);
    if (!(durationMs > 0)) return;
    const timer = setTimeout(() => {
        dismissToast(id);
    }, durationMs);
    timers.set(id, timer);
}

export function getToastSnapshot() {
    return toasts;
}

export function subscribeToasts(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function dismissToast(id) {
    const nextId = String(id || "");
    if (!nextId) return;
    clearTimer(nextId);
    const prev = toasts;
    toasts = toasts.filter((toast) => toast.id !== nextId);
    if (toasts !== prev) emit();
}

export function pushToast({
    type = "info",
    message,
    durationMs = DEFAULT_DURATION_MS,
} = {}) {
    const text = String(message || "").trim();
    if (!text) return null;

    const resolvedType = ["success", "error", "warning", "info"].includes(type)
        ? type
        : "info";
    const resolvedDuration =
        typeof durationMs === "number" && durationMs >= 0
            ? durationMs
            : DEFAULT_DURATION_MS;

    const id = `toast-${Date.now()}-${++idCounter}`;
    const createdAt = Date.now();
    const next = {
        id,
        type: resolvedType,
        message: text,
        durationMs: resolvedDuration,
        createdAt,
    };

    toasts = [next, ...toasts].slice(0, MAX_VISIBLE);
    // Dropped toasts still need timer cleanup if somehow re-pushed; clear extras.
    const keep = new Set(toasts.map((t) => t.id));
    Array.from(timers.keys()).forEach((timerId) => {
        if (!keep.has(timerId)) clearTimer(timerId);
    });

    scheduleAutoDismiss(id, resolvedDuration);
    emit();
    return id;
}

export function queueToastForNextLoad({
    type = "info",
    message,
    durationMs = DEFAULT_DURATION_MS,
} = {}) {
    const text = String(message || "").trim();
    if (!text || typeof sessionStorage === "undefined") {
        return false;
    }

    const resolvedType = ["success", "error", "warning", "info"].includes(type)
        ? type
        : "info";
    const resolvedDuration =
        typeof durationMs === "number" && durationMs >= 0
            ? durationMs
            : DEFAULT_DURATION_MS;

    try {
        sessionStorage.setItem(
            PENDING_TOAST_STORAGE_KEY,
            JSON.stringify({
                type: resolvedType,
                message: text,
                durationMs: resolvedDuration,
            }),
        );
        return true;
    } catch {
        return false;
    }
}

export function flushPendingToast() {
    if (typeof sessionStorage === "undefined") {
        return null;
    }

    try {
        const raw = sessionStorage.getItem(PENDING_TOAST_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        sessionStorage.removeItem(PENDING_TOAST_STORAGE_KEY);
        const parsed = JSON.parse(raw);
        return pushToast(parsed);
    } catch {
        try {
            sessionStorage.removeItem(PENDING_TOAST_STORAGE_KEY);
        } catch {
            // ignore storage cleanup failures
        }
        return null;
    }
}

export const TOAST_DEFAULT_DURATION_MS = DEFAULT_DURATION_MS;
